import {
  SELECTION_LIMIT_MAX,
  SELECTION_LIMIT_MIN,
  SELECTION_MODES,
  type SelectionMode,
} from './shared-question-model-selection.consts';

export function resolveDefaultMaxSelections(optionCount: number): number {
  if (Number.isNaN(optionCount)) {
    return SELECTION_LIMIT_MIN;
  }
  return Math.min(
    SELECTION_LIMIT_MAX,
    Math.max(SELECTION_LIMIT_MIN, Math.floor(optionCount))
  );
}

export interface ClampedSelectionLimits {
  minSelections: number | null;
  maxSelections: number | null;
}

export function clampSelectionLimitsToOptionCount(args: {
  minSelections: number | null | undefined;
  maxSelections: number | null | undefined;
  optionCount: number;
}): ClampedSelectionLimits {
  const { minSelections, maxSelections, optionCount } = args;
  const cap = resolveDefaultMaxSelections(optionCount);

  const clampedMax =
    maxSelections == null ? null : Math.min(maxSelections, cap);
  const clampedMin =
    minSelections == null ? null : Math.min(minSelections, clampedMax ?? cap);

  return { minSelections: clampedMin, maxSelections: clampedMax };
}

export function resolvePersistableSelectionLimits(
  minSelections: number | null | undefined,
  maxSelections: number | null | undefined,
  isChoice: boolean,
  optionCount?: number | null
): ClampedSelectionLimits {
  if (!isChoice) {
    return { minSelections: null, maxSelections: null };
  }

  const limits = resolveRangedSelectionLimits(minSelections, maxSelections);

  if (optionCount == null) return limits;

  return clampSelectionLimitsToOptionCount({ ...limits, optionCount });
}

function resolveRangedSelectionLimits(
  minSelections: number | null | undefined,
  maxSelections: number | null | undefined
): ClampedSelectionLimits {
  const rawMax =
    maxSelections == null || !Number.isFinite(maxSelections)
      ? null
      : Math.floor(maxSelections);
  const rawMin =
    minSelections == null || !Number.isFinite(minSelections)
      ? null
      : Math.floor(minSelections);

  const minInRange =
    rawMin != null &&
    rawMin >= SELECTION_LIMIT_MIN &&
    rawMin <= SELECTION_LIMIT_MAX
      ? rawMin
      : null;

  if (rawMax == null && minInRange != null) {
    return { minSelections: minInRange, maxSelections: null };
  }

  if (rawMax == null) {
    return {
      minSelections: null,
      maxSelections: SELECTION_LIMIT_MIN,
    };
  }

  const max = Math.min(
    SELECTION_LIMIT_MAX,
    Math.max(SELECTION_LIMIT_MIN, rawMax)
  );

  return {
    minSelections: minInRange != null && minInRange <= max ? minInRange : null,
    maxSelections: max,
  };
}

/**
 * Selection-limit truth table shared by the builder (which authors
 * `minSelections`/`maxSelections`) and the responder (which answers against
 * them). A true range (`min` and `max` both set and different) has no
 * dedicated mode; it reads as `UpTo`, using `max` as the displayed count,
 * rather than losing the distinction some other way.
 *
 * | minSelections | maxSelections | mode    |
 * | ------------- | -------------- | ------- |
 * | null           | null           | UpTo    |
 * | set            | null           | AtLeast |
 * | set, === max   | set            | Exactly |
 * | otherwise      | set            | UpTo    |
 */
export function resolveSelectionMode(
  minSelections: number | null | undefined,
  maxSelections: number | null | undefined
): SelectionMode {
  if (maxSelections == null) {
    return minSelections == null
      ? SELECTION_MODES.UpTo
      : SELECTION_MODES.AtLeast;
  }
  if (minSelections != null && minSelections === maxSelections) {
    return SELECTION_MODES.Exactly;
  }
  return SELECTION_MODES.UpTo;
}

/**
 * An upper bound decides single- vs multi-select when present; otherwise a
 * lower bound alone means several selections are allowed. This is what makes
 * "at least N" (stored as `minSelections` set with no `maxSelections`)
 * reachable — treating a missing upper bound as single-select would make
 * that mode unreachable.
 */
export function isMultiSelect(
  minSelections: number | null | undefined,
  maxSelections: number | null | undefined
): boolean {
  if (maxSelections != null) {
    return maxSelections > 1;
  }
  return minSelections != null;
}
